import {
  ICalendarEvent,
  IInteractiveModCalendar,
  interactiveCalendarApps,
  interactiveCalendarTimeZoneModes,
} from "@blings/blings-player";

export const CALENDAR_SOURCE_KEYS = ["value", "expression"] as const;

const CALENDAR_CLEARABLE_SOURCE_KEYS = [
  "value",
  "dataKey",
  "expression",
  "liveControlKey",
  "inputName",
  "defaultValue",
] as const;

export type CalendarSourceKey = typeof CALENDAR_SOURCE_KEYS[number];
type CalendarClearableSourceKey = typeof CALENDAR_CLEARABLE_SOURCE_KEYS[number];
export type CalendarEndMode = "end" | "duration";

export const CALENDAR_EXPRESSION_EXAMPLE = `/*
Full ICalendarEvent reference (all properties):

return {
  title: "Product launch", // Required, non-empty string.
  start: "2026-08-10T18:00", // Required local date/time: YYYY-MM-DDTHH:mm.
  end: "2026-08-10T19:00", // Timed events need end OR durationMinutes.
  // durationMinutes: 60, // Positive whole minutes. Do not use with end.
  allDay: false, // false or omitted = timed event; true = all-day event.
  location: "Azrieli Center, Tel Aviv", // Optional string.
  description: "Join the product launch.", // Optional multiline string.
  url: "https://example.com/launch", // Optional absolute URL.
  reminderMinutes: 15, // Optional whole number >= 0; .ics only.
  uid: "product-launch@example.com", // Optional string; .ics only.
};

Timed start/end values may also be absolute ISO 8601 instants:
  "2026-08-10T15:00:00Z"
  "2026-08-10T18:00:00+03:00"

All-day event (dates are YYYY-MM-DD and end is exclusive):
return {
  title: "Customer conference",
  start: "2026-08-10",
  end: "2026-08-12", // Covers August 10 and 11.
  // durationMinutes: 2880, // Alternative to end; multiples of 1440 only.
  allDay: true,
  location: "Tel Aviv",
  description: "Annual customer conference.",
  url: "https://example.com/conference",
  reminderMinutes: 1440,
  uid: "customer-conference@example.com",
};

The result may be an event object or a JSON-encoded event object.
*/
return {
  title: data.eventTitle,
  start: data.eventStart,
  durationMinutes: 30,
  location: control.eventLocation || data.eventLocation,
};`;

export const getCalendarExpressionFromEvent = (
  event?: Partial<ICalendarEvent>,
  existingExpression?: string
): string => {
  if (
    existingExpression?.trim() &&
    existingExpression !== CALENDAR_EXPRESSION_EXAMPLE
  ) {
    return existingExpression;
  }
  if (event && Object.keys(event).length) {
    return `return ${JSON.stringify(event, null, 2)};`;
  }
  return CALENDAR_EXPRESSION_EXAMPLE;
};

export const CALENDAR_APP_OPTIONS: Array<{
  value: interactiveCalendarApps;
  label: string;
}> = [
  { value: interactiveCalendarApps.system, label: "Calendar app (.ics)" },
  { value: interactiveCalendarApps.ask, label: "Ask viewer" },
  {
    value: interactiveCalendarApps.googleCalendar,
    label: "Google Calendar",
  },
  {
    value: interactiveCalendarApps.outlookCalendar,
    label: "Outlook Calendar",
  },
];

export const CALENDAR_TIME_ZONE_MODE_OPTIONS: Array<{
  value: interactiveCalendarTimeZoneModes;
  label: string;
}> = [
  {
    value: interactiveCalendarTimeZoneModes.viewer,
    label: "Viewer's timezone",
  },
  {
    value: interactiveCalendarTimeZoneModes.fixed,
    label: "Specific timezone",
  },
];

const getOptionLabels = <T extends string>(
  options: Array<{ value: T; label: string }>
) =>
  options.reduce(
    (labels, { value, label }) => ({ ...labels, [value]: label }),
    {} as Record<T, string>
  );

const CALENDAR_APP_LABELS = getOptionLabels(CALENDAR_APP_OPTIONS);
const CALENDAR_TIME_ZONE_MODE_LABELS = getOptionLabels(
  CALENDAR_TIME_ZONE_MODE_OPTIONS
);

const FALLBACK_TIME_ZONES = [
  "UTC",
  "Africa/Johannesburg",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "America/Sao_Paulo",
  "America/Toronto",
  "Asia/Dubai",
  "Asia/Jerusalem",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/Berlin",
  "Europe/London",
  "Europe/Paris",
  "Pacific/Auckland",
];

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const ABSOLUTE_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;
const ABSOLUTE_DATE_TIME_PARTS_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2}(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

type DateParts = Pick<DateTimeParts, "year" | "month" | "day">;

const hasText = (value: unknown): value is string =>
  typeof value === "string" && Boolean(value.trim());

const getDateParts = (value: string): DateParts | null => {
  const match = DATE_PATTERN.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const getLocalDateTimeParts = (value: string): DateTimeParts | null => {
  const match = LOCAL_DATE_TIME_PATTERN.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] || 0),
  };
};

const areValidDateParts = ({ year, month, day }: DateParts) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const areValidDateTimeParts = (parts: DateTimeParts) => {
  if (
    parts.hour < 0 ||
    parts.hour > 23 ||
    parts.minute < 0 ||
    parts.minute > 59 ||
    parts.second < 0 ||
    parts.second > 59
  ) {
    return false;
  }
  return areValidDateParts(parts);
};

export const isValidCalendarDate = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const parts = getDateParts(value);
  return Boolean(parts && areValidDateParts(parts));
};

export const isLocalCalendarDateTime = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const parts = getLocalDateTimeParts(value);
  return Boolean(parts && areValidDateTimeParts(parts));
};

export const isValidCalendarDateTime = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  if (isLocalCalendarDateTime(value)) return true;
  const wallClockValue = value.replace(/(?:Z|[+-]\d{2}:\d{2})$/, "");
  return (
    ABSOLUTE_DATE_TIME_PATTERN.test(value) &&
    isLocalCalendarDateTime(wallClockValue) &&
    Number.isFinite(Date.parse(value))
  );
};

export const getCalendarPickerDateTime = (value?: string) => {
  if (!value) return value;
  const absoluteParts = ABSOLUTE_DATE_TIME_PARTS_PATTERN.exec(value);
  return absoluteParts ? `${absoluteParts[1]}${absoluteParts[2] || ""}` : value;
};

export const formatCalendarPickerDateTime = (
  value: string,
  previousValue?: string
) => {
  const absoluteParts = previousValue
    ? ABSOLUTE_DATE_TIME_PARTS_PATTERN.exec(previousValue)
    : null;
  return absoluteParts
    ? `${value}${absoluteParts[2] || ""}${absoluteParts[3]}`
    : value;
};

const formatCalendarDate = (date: Date) =>
  `${date.getUTCFullYear().toString().padStart(4, "0")}-${(
    date.getUTCMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${date.getUTCDate().toString().padStart(2, "0")}`;

export const addCalendarDays = (
  value: string | undefined,
  days: number
): string | undefined => {
  if (!value || !isValidCalendarDate(value)) return undefined;
  const parts = getDateParts(value)!;
  const date = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day + days)
  );
  return formatCalendarDate(date);
};

export const getInclusiveAllDayEnd = (exclusiveEnd?: string) =>
  addCalendarDays(exclusiveEnd, -1);

export const getExclusiveAllDayEnd = (inclusiveEnd?: string) =>
  addCalendarDays(inclusiveEnd, 1);

export const createEmptyCalendarEvent = (): ICalendarEvent =>
  ({} as ICalendarEvent);

export const updateCalendarEventField = (
  event: ICalendarEvent,
  key: keyof ICalendarEvent,
  value: unknown
): ICalendarEvent => {
  const next = { ...event } as Record<string, unknown>;
  if (
    value === "" ||
    value === null ||
    typeof value === "undefined" ||
    (key === "allDay" && value === false)
  ) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next as ICalendarEvent;
};

export const getCalendarEndMode = (
  event?: Partial<ICalendarEvent>
): CalendarEndMode =>
  typeof event?.durationMinutes !== "undefined" ? "duration" : "end";

export const setCalendarEndMode = (
  event: ICalendarEvent,
  mode: CalendarEndMode
): ICalendarEvent => {
  const next = { ...event };
  if (mode === "end") delete next.durationMinutes;
  else delete next.end;
  return next;
};

const toAllDayDate = (value?: string) => {
  if (!value) return undefined;
  if (isValidCalendarDate(value)) return value;
  const datePart = value.slice(0, 10);
  return isValidCalendarDate(datePart) ? datePart : undefined;
};

const toTimedDate = (value?: string) =>
  value && isValidCalendarDate(value) ? `${value}T09:00` : undefined;

export const setCalendarAllDay = (
  event: ICalendarEvent,
  allDay: boolean
): ICalendarEvent => {
  if (allDay) {
    const start = toAllDayDate(event.start);
    let end = toAllDayDate(event.end);
    if (start && end && end <= start) end = addCalendarDays(start, 1);
    const next: ICalendarEvent = {
      ...event,
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      allDay: true,
    };
    if (!start) delete next.start;
    if (!end) delete next.end;
    delete next.durationMinutes;
    return next;
  }

  const start = toTimedDate(event.start);
  const next: ICalendarEvent = {
    ...event,
    ...(start ? { start } : {}),
  };
  delete next.allDay;
  delete next.end;
  delete next.durationMinutes;
  if (!start) delete next.start;
  return next;
};

export const getActiveCalendarSource = (
  change: Partial<IInteractiveModCalendar>
): CalendarSourceKey =>
  CALENDAR_SOURCE_KEYS.find(
    (key) => typeof (change as Record<string, unknown>)[key] !== "undefined"
  ) || "value";

export const getCalendarSourceUpdate = (
  source: CalendarSourceKey,
  sourceValue: unknown
): { keys: CalendarClearableSourceKey[]; values: unknown[] } => ({
  keys: [...CALENDAR_CLEARABLE_SOURCE_KEYS],
  values: CALENDAR_CLEARABLE_SOURCE_KEYS.map((key) =>
    key === source ? sourceValue : undefined
  ),
});

export const getSupportedCalendarTimeZones = (): string[] => {
  const supportedValuesOf = (Intl as any).supportedValuesOf as
    | ((key: string) => string[])
    | undefined;
  if (!supportedValuesOf) return [...FALLBACK_TIME_ZONES];
  try {
    return Array.from(
      new Set(["UTC", ...supportedValuesOf.call(Intl, "timeZone")])
    );
  } catch {
    return [...FALLBACK_TIME_ZONES];
  }
};

export const isValidCalendarTimeZone = (value: unknown): value is string => {
  if (!hasText(value)) return false;
  if (/^(?:UTC|GMT)?[+-]\d{1,2}(?::?\d{2})?$/i.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
};

const getFormattedDateTimeParts = (
  formatter: Intl.DateTimeFormat,
  epoch: number
): DateTimeParts => {
  const values = Object.fromEntries(
    formatter
      .formatToParts(new Date(epoch))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)])
  );
  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  };
};

const getDateTimePartsEpoch = (parts: DateTimeParts) =>
  Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

const areEqualDateTimeParts = (left: DateTimeParts, right: DateTimeParts) =>
  left.year === right.year &&
  left.month === right.month &&
  left.day === right.day &&
  left.hour === right.hour &&
  left.minute === right.minute &&
  left.second === right.second;

const getZonedDateTimeEpoch = (
  value: string,
  timeZone: string
): number | null => {
  const parts = getLocalDateTimeParts(value);
  if (!parts || !isValidCalendarTimeZone(timeZone)) return null;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const localAsUtc = getDateTimePartsEpoch(parts);
  let candidate = localAsUtc;

  // Usually converges in two formats. The final bounded pass detects DST gaps,
  // which oscillate between the instants immediately before and after the gap.
  for (let pass = 0; pass < 3; pass += 1) {
    const formattedParts = getFormattedDateTimeParts(formatter, candidate);
    if (areEqualDateTimeParts(formattedParts, parts)) return candidate;
    candidate += localAsUtc - getDateTimePartsEpoch(formattedParts);
  }
  return null;
};

const getComparableTimedValue = (
  value: string,
  timeZoneMode?: interactiveCalendarTimeZoneModes,
  timeZone?: string
) => {
  if (!isLocalCalendarDateTime(value)) return Date.parse(value);
  if (timeZoneMode === interactiveCalendarTimeZoneModes.fixed && timeZone) {
    return getZonedDateTimeEpoch(value, timeZone);
  }
  const parts = getLocalDateTimeParts(value)!;
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
};

const parseCalendarEvent = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || !value.trim().startsWith("{")) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

export const parseCalendarEventValue = (
  value: unknown
): ICalendarEvent | undefined =>
  (parseCalendarEvent(value) as ICalendarEvent | null) || undefined;

const hasValidEventUrl = (value: unknown) => {
  if (!hasText(value)) return true;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

export const validateCalendarEvent = (
  value: unknown,
  timeZoneMode?: interactiveCalendarTimeZoneModes,
  timeZone?: string
): string[] => {
  const event = parseCalendarEvent(value);
  if (!event) return ["Choose an event source."];

  const errors: string[] = [];
  const allDay = event.allDay === true;
  const timedValueCache = new Map<string, number | null>();
  const getTimedValue = (dateTime: string) => {
    if (!timedValueCache.has(dateTime)) {
      timedValueCache.set(
        dateTime,
        getComparableTimedValue(dateTime, timeZoneMode, timeZone)
      );
    }
    return timedValueCache.get(dateTime)!;
  };
  if (!hasText(event.title)) errors.push("Enter an event title.");
  if (!hasText(event.start)) {
    errors.push("Enter the event start.");
  } else if (allDay) {
    if (!isValidCalendarDate(event.start)) {
      errors.push("Use a valid calendar date.");
    }
  } else if (!isValidCalendarDateTime(event.start)) {
    errors.push("Use a valid date and time.");
  } else if (
    isLocalCalendarDateTime(event.start) &&
    timeZoneMode === interactiveCalendarTimeZoneModes.fixed &&
    timeZone &&
    getTimedValue(event.start) === null
  ) {
    errors.push("Use a valid date and time.");
  }

  const hasEnd = typeof event.end !== "undefined" && event.end !== "";
  const hasDuration = typeof event.durationMinutes !== "undefined";
  if (hasEnd && hasDuration) {
    errors.push("Choose either an end time or a duration, not both.");
  }
  if (!allDay && !hasEnd && !hasDuration) {
    errors.push("Enter an end time or a duration.");
  }

  if (hasEnd) {
    const isValidEnd = allDay
      ? isValidCalendarDate(event.end)
      : isValidCalendarDateTime(event.end);
    if (!isValidEnd) {
      errors.push(
        allDay ? "Use a valid calendar date." : "Use a valid date and time."
      );
    } else if (
      !allDay &&
      isLocalCalendarDateTime(event.end) &&
      timeZoneMode === interactiveCalendarTimeZoneModes.fixed &&
      timeZone &&
      getTimedValue(event.end) === null
    ) {
      errors.push("Use a valid date and time.");
    } else if (hasText(event.start)) {
      const startComparable = allDay ? event.start : getTimedValue(event.start);
      const endComparable = allDay
        ? event.end
        : getTimedValue(event.end as string);
      if (
        startComparable !== null &&
        endComparable !== null &&
        endComparable <= startComparable
      ) {
        errors.push("The event must end after it starts.");
      }
    }
  }

  if (hasDuration) {
    const duration = event.durationMinutes;
    const validNumber =
      typeof duration === "number" &&
      Number.isFinite(duration) &&
      duration > 0 &&
      Number.isInteger(duration);
    if (!validNumber || (allDay && (duration as number) % 1440 !== 0)) {
      errors.push(
        allDay
          ? "All-day duration must be a whole number of days."
          : "Enter an end time or a duration."
      );
    }
  }

  if (
    typeof event.reminderMinutes !== "undefined" &&
    (!Number.isInteger(event.reminderMinutes) || event.reminderMinutes < 0)
  ) {
    errors.push("Reminder must be a whole number of minutes, zero or greater.");
  }
  if (!hasValidEventUrl(event.url)) {
    errors.push("Enter a valid event URL.");
  }

  return Array.from(new Set(errors));
};

export const validateCalendarMod = (
  change: Partial<IInteractiveModCalendar>
): string[] => {
  const errors: string[] = [];
  if (!hasText(change.layerName)) {
    errors.push("Select at least one interactive layer.");
  }
  if (!hasText(change.ctaName)) {
    errors.push("Name this button for analytics.");
  }
  if (
    !change.app ||
    !CALENDAR_APP_OPTIONS.some(({ value }) => value === change.app)
  ) {
    errors.push("Choose a calendar application.");
  }
  if (!change.timeZoneMode) errors.push("Choose timezone behavior.");

  const source = getActiveCalendarSource(change);
  const sourceValue = (change as Record<string, unknown>)[source];
  const staticEvent =
    source === "value" ? parseCalendarEvent(sourceValue) : null;
  const hasSource =
    typeof sourceValue !== "undefined" &&
    (typeof sourceValue !== "string" || Boolean(sourceValue.trim()));
  if (!hasSource) {
    errors.push("Choose an event source.");
  } else if (source === "value") {
    errors.push(
      ...validateCalendarEvent(
        sourceValue,
        change.timeZoneMode,
        change.timeZone
      )
    );
  }
  // Code may depend on viewer-time data and controls, so Studio can only
  // validate that it is present. The Player validates the returned event.

  if (
    change.timeZoneMode === interactiveCalendarTimeZoneModes.fixed &&
    (source !== "value" || staticEvent?.allDay !== true) &&
    !isValidCalendarTimeZone(change.timeZone)
  ) {
    errors.push("Choose a valid IANA timezone.");
  }
  return Array.from(new Set(errors));
};

export const getCalendarProviderHelp = (
  app?: interactiveCalendarApps
): string | undefined => {
  if (
    app === interactiveCalendarApps.googleCalendar ||
    app === interactiveCalendarApps.outlookCalendar
  ) {
    return "Custom reminders and event UID are not transferred to this calendar provider.";
  }
  if (app === interactiveCalendarApps.ask) {
    return "Custom reminders and event UID apply only when the viewer chooses Calendar app (.ics).";
  }
  return undefined;
};

export const getCalendarSummary = (
  change: Partial<IInteractiveModCalendar>
): string => {
  const source = getActiveCalendarSource(change);
  const event = parseCalendarEvent(change.value);
  let eventLabel = "Calendar event";
  if (source === "value" && event) {
    eventLabel =
      event.allDay === true ? "All day" : String(event.title || eventLabel);
    if (event.allDay === true && hasText(event.title)) {
      eventLabel += ` · ${event.title.trim()}`;
    }
  } else if (source === "expression") {
    eventLabel = "Code";
  }
  const timeZoneLabel =
    change.timeZoneMode === interactiveCalendarTimeZoneModes.fixed
      ? change.timeZone ||
        CALENDAR_TIME_ZONE_MODE_LABELS[interactiveCalendarTimeZoneModes.fixed]
      : CALENDAR_TIME_ZONE_MODE_LABELS[interactiveCalendarTimeZoneModes.viewer];
  return [
    (change.app && CALENDAR_APP_LABELS[change.app]) || "Calendar",
    eventLabel,
    timeZoneLabel,
  ]
    .filter(Boolean)
    .join(" · ");
};
